"use client";

import React from "react";
import * as m from "motion/react-m";
import { ArrowDown } from "lucide-react";
import { Button } from "./ui/button";
import { aboutMe } from "#/data";
import dynamic from "next/dynamic";
import { useSections } from "#/contexts/sections-context";
import { handleSectionClick } from "#/lib/utils/scroll";
const MatrixEffect = dynamic(() => import("./matrix-effect").then((mod) => ({ default: mod.MatrixEffect })), {
  ssr: false,
  loading: () => (
    <div className="relative flex aspect-square w-[min(90vw,70vh)] items-center justify-center overflow-hidden rounded-full bg-black" />
  ),
});

export function HeroSection() {
  const { preloadSectionsUntil } = useSections();

  return (
    <section id="hero" className="bg-background relative min-h-screen w-full">
      <div className="container mx-auto flex min-h-[calc(100vh-4rem)] max-w-7xl flex-col-reverse items-center justify-center gap-8 px-4 py-12 md:flex-row md:gap-12">
        <m.div
          initial={{ opacity: 0.1, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="z-30 flex w-full flex-col items-center pt-16 text-center sm:pt-8 md:w-2/5 md:items-start md:pt-0 md:text-left"
        >
          <m.span
            className="text-primary inline-block font-medium"
            initial={{ opacity: 0.1 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <span className="text-sm tracking-widest uppercase">Développeur Full Stack</span>
          </m.span>

          <m.h1
            className="mt-2 !text-4xl leading-tight font-bold md:!text-5xl lg:!text-6xl"
            initial={{ opacity: 0.1 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {aboutMe.name}
          </m.h1>

          <m.h2
            className="text-muted-foreground dark:text-muted-foreground mt-2 text-2xl md:text-3xl"
            initial={{ opacity: 0.1 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Architecte Web & Développeur Freelance
          </m.h2>

          <m.div
            className="mt-6 flex flex-wrap justify-center gap-4 md:justify-start"
            initial={{ opacity: 0.1 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <Button
              asChild
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground dark:bg-primary dark:hover:bg-primary/90 dark:text-primary-foreground"
            >
              <a href="#contact" onClick={(e) => handleSectionClick(e, "#contact", preloadSectionsUntil)}>
                Me contacter
              </a>
            </Button>

            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-primary text-primary hover:bg-primary/10 dark:border-primary dark:text-primary dark:hover:bg-primary/10"
            >
              <a href="#timeline" onClick={(e) => handleSectionClick(e, "#timeline", preloadSectionsUntil)}>
                Voir mon parcours
                <ArrowDown className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </m.div>
        </m.div>

        {/* Canvas circulaire - à droite sur desktop, en haut sur mobile */}
        <m.div
          initial={{ opacity: 0.1, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex w-full items-center justify-center pt-16 md:w-3/5 md:translate-y-8 md:pt-0"
        >
          <div className="border-primary/30 relative flex aspect-square w-[min(90vw,70vh)] items-center justify-center overflow-hidden rounded-full border-4 bg-black md:w-[min(45vw,65vh)] dark:border-0">
            <MatrixEffect className="absolute inset-0 h-full w-full object-cover" />
          </div>
        </m.div>
      </div>

      {/* Indicateur de défilement */}
      <m.div
        className="absolute bottom-3 left-1/2 z-50 flex -translate-x-1/2 transform flex-col items-center sm:bottom-6 md:bottom-8"
        initial={{ opacity: 0.1, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.5 }}
      >
        <m.div
          className="border-primary flex h-10 w-6 justify-center rounded-full border-2 p-1"
          animate={{ y: [0, 5, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          <m.div className="bg-primary h-1.5 w-1.5 rounded-full" />
        </m.div>
      </m.div>
    </section>
  );
}
